import { ref, computed, readonly } from "vue";
import axios from "axios";
import { App } from "@capacitor/app";

/**
 * App-wide connectivity / API-health state machine.
 *
 * Two independent signals feed a single derived status that the
 * ConnectivityBanner binds to:
 *   - deviceOnline: navigator.onLine + window online/offline events.
 *   - apiHealth:    driven by the axios response interceptor (success → healthy,
 *                   5xx / no-response → degraded) and the recovery probe.
 *
 * Device-offline always supersedes an API outage (we can't reach the API anyway),
 * so a dropped connection never shows a false "outage" message.
 *
 * This module is a singleton: state lives at module scope so every importer —
 * the interceptor, the banner, the prediction queue — shares the same refs.
 */

export type ApiHealth = "healthy" | "degraded" | "unknown";
export type ConnectivityStatus = "ONLINE" | "OFFLINE" | "OUTAGE";

const HEALTH_ENDPOINT = "up"; // Laravel /up, resolved against axios baseURL
const RECOVERY_INTERVAL_MS = 15_000;

const deviceOnline = ref(
    typeof navigator !== "undefined" ? navigator.onLine : true,
);
const apiHealth = ref<ApiHealth>("unknown");

const connectivityStatus = computed<ConnectivityStatus>(() => {
    if (!deviceOnline.value) return "OFFLINE";
    if (apiHealth.value === "degraded") return "OUTAGE";
    return "ONLINE";
});

let recoveryTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let probing = false;

/** Listeners that want to react to "we just recovered" (e.g. the queue flush). */
const recoveryListeners = new Set<() => void>();

function notifyRecovered() {
    recoveryListeners.forEach((cb) => {
        try {
            cb();
        } catch {
            // a listener must never break recovery
        }
    });
}

function stopRecoveryLoop() {
    if (recoveryTimer) {
        clearInterval(recoveryTimer);
        recoveryTimer = null;
    }
}

function startRecoveryLoop() {
    if (recoveryTimer || !deviceOnline.value) return;
    recoveryTimer = setInterval(() => {
        void recheck();
    }, RECOVERY_INTERVAL_MS);
}

/**
 * Probe the cheap, unauthenticated Laravel /up health endpoint. A 2xx flips us
 * back to healthy; a failure leaves us degraded so the loop keeps trying.
 */
async function recheck(): Promise<void> {
    if (probing || !deviceOnline.value) return;
    probing = true;
    try {
        await axios.get(HEALTH_ENDPOINT);
        reportApiSuccess();
    } catch {
        // still down — stay degraded, the loop will retry
    } finally {
        probing = false;
    }
}

/** Called by the axios response interceptor on any successful (2xx) response. */
function reportApiSuccess() {
    const wasDegraded = apiHealth.value === "degraded";
    apiHealth.value = "healthy";
    stopRecoveryLoop();
    if (wasDegraded) {
        notifyRecovered();
    }
}

/**
 * Called by the axios response interceptor on failure.
 *  - "5xx": server is up but erroring / in maintenance → degraded.
 *  - "no-response": network error / timeout → only degraded when the device
 *    believes it is online (otherwise OFFLINE already covers it; we don't want a
 *    false outage the instant the connection drops).
 */
function reportApiError(kind: "5xx" | "no-response") {
    if (kind === "no-response" && !deviceOnline.value) {
        return;
    }
    apiHealth.value = "degraded";
    startRecoveryLoop();
}

function handleOffline() {
    deviceOnline.value = false;
    stopRecoveryLoop();
}

function handleOnline() {
    deviceOnline.value = true;
    // Connection is back but the API health is unknown until proven — probe it.
    void recheck();
    startRecoveryLoop();
}

function init() {
    if (initialized) return;
    initialized = true;

    deviceOnline.value =
        typeof navigator !== "undefined" ? navigator.onLine : true;

    if (typeof window !== "undefined") {
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
    }

    // Foreground / resume: connection or API may have changed while backgrounded.
    App.addListener("resume", () => {
        deviceOnline.value =
            typeof navigator !== "undefined" ? navigator.onLine : true;
        if (deviceOnline.value && apiHealth.value === "degraded") {
            void recheck();
        }
    }).catch(() => {
        // App plugin unavailable (e.g. web) — non-fatal
    });
}

function onRecovered(cb: () => void): () => void {
    recoveryListeners.add(cb);
    return () => recoveryListeners.delete(cb);
}

export function useConnectivity() {
    return {
        deviceOnline: readonly(deviceOnline),
        apiHealth: readonly(apiHealth),
        connectivityStatus,
        init,
        recheck,
        reportApiSuccess,
        reportApiError,
        onRecovered,
    };
}
